<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                <v-icon class="me-2">mdi-card-account-details</v-icon>
                Job Card Report
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form @submit.prevent="fetchSheet">
                  <v-row class="py-3 px-3" align="end" dense>
                    <v-col cols="12" md="3">
                      <v-date-input input-format="dd/mm/yyyy" v-model="filters.from_date" label="From Date"
                        variant="outlined" density="compact" bg-color="grey-lighten-4" prepend-icon=""
                        prepend-inner-icon="mdi-calendar" hide-details="auto" clearable :max="filters.to_date" />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-date-input input-format="dd/mm/yyyy" v-model="filters.to_date" label="To Date"
                        variant="outlined" density="compact" bg-color="grey-lighten-4" prepend-icon=""
                        prepend-inner-icon="mdi-calendar" hide-details="auto" clearable :min="filters.from_date" />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-autocomplete v-model="filters.employee_id" :items="employees" :item-title="formatEmployeeLabel"
                        item-value="id" label="Employee (Required)" variant="outlined" density="compact"
                        bg-color="grey-lighten-4" :loading="employeesLoading" clearable hide-details="auto"
                        placeholder="Search employee" />
                    </v-col>
                    <v-col cols="12" md="3" class="d-flex align-end">
                      <v-btn type="submit" color="primary" variant="flat" :loading="loading" prepend-icon="mdi-magnify"
                        density="compact" height="40">
                        Search
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>

        <v-col cols="12">
          <v-expansion-panels v-model="reportPanel" class="mt-3">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header" expand-icon="mdi-chevron-down">
                <v-icon class="me-2">mdi-table</v-icon>
                Job Card Details
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-row class="py-2">


                  <v-col md="12" cols="12">
                    <div class="d-flex align-center flex-column">
                      <v-btn-group v-if="sheetData.length > 0" variant="outlined" density="compact" size="x-small"
                        divided>
                        <v-btn prepend-icon="mdi mdi-download" variant="flat" color="success" @click="exportToExcel"
                          :disabled="!sheetData.length">EXCEL</v-btn>
                        <v-btn prepend-icon="mdi mdi-download" variant="flat" color="red" @click="exportToPDF"
                          :disabled="!sheetData.length">PDF</v-btn>
                      </v-btn-group>
                    </div>
                  </v-col>

                  <v-col md="3" cols="12"></v-col>
                </v-row>

                <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

                <v-alert v-if="!loading && filteredSheetData.length === 0" type="info" variant="tonal">
                  No job card records found for the selected criteria.
                </v-alert>

                <!-- Employee Information Section -->
                <div v-if="filteredSheetData.length > 0 && selectedEmployeeInfo" class="mb-4">
                  <v-card variant="outlined" class="pa-4">
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-row dense>
                          <v-col cols="4"><strong>Employee Code:</strong></v-col>
                          <v-col cols="8">{{ selectedEmployeeInfo.employee_code || '-' }}</v-col>
                          <v-col cols="4"><strong>Employee Name:</strong></v-col>
                          <v-col cols="8">{{ selectedEmployeeInfo.employee_name || '-' }}</v-col>
                          <v-col cols="4"><strong>Join Date:</strong></v-col>
                          <v-col cols="8">{{ formatDateDash(selectedEmployeeInfo.joining_date) || '-' }}</v-col>

                        </v-row>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-row dense>
                          <v-col cols="4"><strong>Designation:</strong></v-col>
                          <v-col cols="8">{{ selectedEmployeeInfo.designation || '-' }}</v-col>
                          <v-col cols="4"><strong>Branch:</strong></v-col>
                          <v-col cols="8">{{ selectedEmployeeInfo.branch_name || '-' }}</v-col>

                          <v-col cols="4"><strong>Department:</strong></v-col>
                          <v-col cols="8">{{ selectedEmployeeInfo.department || '-' }}</v-col>
                        </v-row>
                      </v-col>
                    </v-row>
                  </v-card>
                </div>

                <!-- Attendance Details Table -->
                <div v-if="filteredSheetData.length">
                  <v-table density="comfortable" class="text-caption table-bordered ">
                    <thead>
                      <tr>
                        <th style="text-align: center;">Attendance Date</th>
                        <th style="text-align: center;">Status</th>
                        <th style="text-align: center;">Expected Duty (Hour)</th>
                        <th style="text-align: center;">In Time</th>
                        <th style="text-align: center;">Out Time</th>
                        <th style="text-align: center;">Break Time (Hour)</th>
                        <th style="text-align: center;">Actual Duty (Hour)</th>
                        <th style="text-align: center;">Extra/Less Duty (Hour)</th>
                        <th>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, index) in filteredSheetData" :key="`${row.employee_code}-${row.date}-${index}`">
                        <td>{{ (row.date) }}</td>
                        <td>{{ (row.status?.name) }}</td>
                        <td style="text-align: center;">{{ getExpectedDutyHour(row) }}</td>
                        <td style="text-align: center;">{{ formatTime(row.punch_in) || '-' }}</td>
                        <td style="text-align: center;">{{ formatTime(row.punch_out) || '-' }}</td>
                        <td style="text-align: center;">{{ formatBreakTime(row) }}</td>
                        <td style="text-align: center;">{{ formatActualDutyHour(row) || '-' }}</td>
                        <td style="text-align: center;">{{ formatExtraLessDuty(row) || '-' }}</td>
                        <td style="text-align: center;">{{ row.message || '-' }}</td>
                      </tr>
                    </tbody>
                  </v-table>
                </div>

                <!-- Summary Statistics Section -->
                <div v-if="filteredSheetData.length > 0" class="mt-4">
                  <v-card variant="outlined" class="pa-4">
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-row dense>
                          <v-col cols="6"><strong>Present:</strong></v-col>
                          <v-col cols="6">{{ jobCardSummary.present }}</v-col>
                          <v-col cols="6"><strong>Delay:</strong></v-col>
                          <v-col cols="6">{{ jobCardSummary.delay }}</v-col>
                          <v-col cols="6"><strong>Leave:</strong></v-col>
                          <v-col cols="6">{{ jobCardSummary.leave }}</v-col>
                          <v-col cols="6"><strong>Weekend:</strong></v-col>
                          <v-col cols="6">{{ jobCardSummary.weekend }}</v-col>
                          <v-col cols="6"><strong>Expected Duty Hour:</strong></v-col>
                          <v-col cols="6">{{ formatHoursMinutes(jobCardSummary.expected_duty_hours) }}</v-col>
                          <v-col cols="6"><strong>Total Days (Available in office):</strong></v-col>
                          <v-col cols="6">{{ filteredSheetData.length }} ({{ jobCardSummary.present +
                            jobCardSummary.delay }})</v-col>
                        </v-row>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-row dense>
                          <v-col cols="6"><strong>Absent:</strong></v-col>
                          <v-col cols="6">{{ jobCardSummary.absent }}</v-col>
                          <v-col cols="6"><strong>Extreme Delay:</strong></v-col>
                          <v-col cols="6">{{ jobCardSummary.extreme_delay }}</v-col>
                          <v-col cols="6"><strong>Visit:</strong></v-col>
                          <v-col cols="6">{{ jobCardSummary.visit }}</v-col>
                          <v-col cols="6"><strong>Holiday:</strong></v-col>
                          <v-col cols="6">{{ jobCardSummary.holiday }}</v-col>
                          <v-col cols="6"><strong>Actual Duty Hour:</strong></v-col>
                          <v-col cols="6">{{ formatHoursMinutes(jobCardSummary.actual_duty_hours) }}</v-col>
                          <v-col cols="6"><strong>Extra/Less Duty Hours:</strong></v-col>
                          <v-col cols="6">{{ formatHoursMinutes(jobCardSummary.extra_less_duty_hours, true) }}</v-col>
                        </v-row>
                      </v-col>
                    </v-row>
                  </v-card>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { ref, reactive, computed, onMounted, getCurrentInstance } from 'vue'
import { toast } from 'vue3-toastify'
import { useStore } from 'vuex'
import { formatDateView, formatDateDash } from "@/plugins/helpers";


const store = useStore()
const instance = getCurrentInstance()
const appContext = instance?.appContext

const employees = ref([])
const employeesLoading = ref(false)
const loading = ref(false)
const sheetData = ref([])
const panel = ref([0])
const reportPanel = ref([0])
const search = ref('')

const filters = reactive({
  from_date: '',
  to_date: '',
  employee_id: null
})

const formatEmployeeLabel = item => {
  if (!item) return ''
  return `${item.name || ''}${item.employee_code ? ` - ${item.employee_code}` : ''}`
}



const formatNumber = (value, fraction = 0) => {
  if (value === undefined || value === null || isNaN(value)) return '-'
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: fraction,
    maximumFractionDigits: fraction
  })
}

const extractTimeFromDatetime = (val) => {
  if (!val) return ''
  const str = typeof val === 'string' ? val : val
  const spaceIdx = str.indexOf(' ')
  return spaceIdx >= 0 ? str.slice(spaceIdx + 1) : str
}

const formatTime = (timeStr) => {
  if (!timeStr || timeStr === '-') return '-'

  // Handle time strings like "08:30:00" or "08:30"
  const timeParts = String(timeStr).split(':')
  if (timeParts.length < 2) return timeStr

  const hours = parseInt(timeParts[0], 10)
  const minutes = timeParts[1]

  if (isNaN(hours)) return timeStr

  const period = hours >= 12 ? 'PM' : 'AM'
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours

  return `${displayHours}:${minutes} ${period}`
}

// Filtered sheet data based on search
const filteredSheetData = computed(() => {
  if (!search.value) {
    return sheetData.value
  }

  const searchLower = search.value.toLowerCase()
  return sheetData.value.filter(row => {
    const employeeCode = (row.employee_code || '').toLowerCase()
    const employeeName = (row.employee_name || '').toLowerCase()
    const department = (row.department || '').toLowerCase()
    const designation = (row.designation || '').toLowerCase()
    return employeeCode.includes(searchLower) ||
      employeeName.includes(searchLower) ||
      department.includes(searchLower) ||
      designation.includes(searchLower)
  })
})

// Computed totals
const totals = computed(() => {
  return sheetData.value.reduce(
    (acc, row) => {
      acc.working_hours += Number(row.working_hours) || 0
      acc.late_minutes += Number(row.late_minutes) || 0
      acc.early_leaving_minutes += Number(row.early_leaving_minutes) || 0
      acc.overtime_hours += (Number(row.overtime_minutes) || 0) / 60
      return acc
    },
    {
      working_hours: 0,
      late_minutes: 0,
      early_leaving_minutes: 0,
      overtime_hours: 0
    }
  )
})

// Computed totals for filtered data
const filteredTotals = computed(() => {
  return filteredSheetData.value.reduce(
    (acc, row) => {
      acc.working_hours += Number(row.working_hours) || 0
      acc.late_minutes += Number(row.late_minutes) || 0
      acc.early_leaving_minutes += Number(row.early_leaving_minutes) || 0
      acc.overtime_hours += (Number(row.overtime_minutes) || 0) / 60
      return acc
    },
    {
      working_hours: 0,
      late_minutes: 0,
      early_leaving_minutes: 0,
      overtime_hours: 0
    }
  )
})

const statusColor = status => {
  switch (status) {
    case 'Present':
      return 'success'
    case 'Late Check In':
    case 'Late Check In & Early Checkout':
      return 'warning'
    case 'Early Checkout':
      return 'purple'
    case 'Absent':
      return 'error'
    case 'Leave':
      return 'blue'
    case 'Weekend':
    case 'Holiday':
      return 'teal'
    default:
      return 'grey'
  }
}





// Get expected duty hour from shift
const getExpectedDutyHour = (row) => {
  if (!row.shift?.in_time || !row.shift?.out_time) return '-'
  if (['Weekend', 'Holiday', 'Absent'].includes(row.status?.name)) return '-'

  const inTime = row.shift.in_time.split(':')
  const outTime = row.shift.out_time.split(':')
  const inHour = parseInt(inTime[0])
  const inMin = parseInt(inTime[1])
  const outHour = parseInt(outTime[0])
  const outMin = parseInt(outTime[1])

  let totalMinutes = (outHour * 60 + outMin) - (inHour * 60 + inMin)
  if (totalMinutes < 0) totalMinutes += 24 * 60 // Handle night shift

  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

// Format break time (currently always 00:00 based on image)
const formatBreakTime = (row) => {
  // Break time calculation can be added if backend provides break data
  return '00:00'
}

// Format actual duty hour
const formatActualDutyHour = (row) => {
  if (!row.working_hours || row.working_hours === 0) {
    if (['Weekend', 'Holiday', 'Absent'].includes(row.status?.name)) return '-'
    return '-'
  }

  const totalMinutes = Math.round(row.working_hours * 60)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

// Format extra/less duty hour
const formatExtraLessDuty = (row) => {
  if (!row.shift?.in_time || !row.shift?.out_time || !row.working_hours) {
    if (['Weekend', 'Holiday'].includes(row.status?.name)) return '-'
    if (row.status?.name === 'Absent') {
      // For absent, show -09:00 (expected duty hours as negative)
      const expected = getExpectedDutyHour(row)
      if (expected === '-') return '-'
      return `- ${expected}`
    }
    return '-'
  }

  const expected = getExpectedDutyHour(row)
  if (expected === '-') return '-'

  const [expHours, expMins] = expected.split(':').map(Number)
  const expectedMinutes = expHours * 60 + expMins
  const actualMinutes = Math.round(row.working_hours * 60)
  const diffMinutes = actualMinutes - expectedMinutes

  if (diffMinutes === 0) return '00:00'

  const absDiff = Math.abs(diffMinutes)
  const hours = Math.floor(absDiff / 60)
  const minutes = absDiff % 60
  const sign = diffMinutes < 0 ? '- ' : ''
  return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

// Format hours:minutes from total minutes
const formatHoursMinutes = (totalMinutes, showSign = false) => {
  if (totalMinutes === undefined || totalMinutes === null) return '00:00'
  const absMinutes = Math.abs(totalMinutes)
  const hours = Math.floor(absMinutes / 60)
  const minutes = absMinutes % 60
  const sign = showSign && totalMinutes < 0 ? '- ' : ''
  return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

// Selected employee info from first row
const selectedEmployeeInfo = computed(() => {
  if (sheetData.value.length === 0) return null
  const firstRow = sheetData.value[0]
  return {
    employee_code: firstRow.employee_code,
    employee_name: firstRow.employee_name,
    joining_date: firstRow.joining_date,
    status: 'Active', // Can be enhanced if backend provides status
    designation: firstRow.designation,
    branch_name: firstRow.branch_name,
    department: firstRow.department
  }
})

// Job card summary statistics
const jobCardSummary = computed(() => {
  const summary = {
    present: 0,
    delay: 0,
    absent: 0,
    leave: 0,
    weekend: 0,
    holiday: 0,
    extreme_delay: 0,
    visit: 0,
    expected_duty_hours: 0,
    actual_duty_hours: 0,
    extra_less_duty_hours: 0
  }

  filteredSheetData.value.forEach(row => {
    const statusName = row.status?.name || ''

    // Count statuses
    if (statusName === 'Present') {
      summary.present++
    } else if (statusName === 'Late Check In' || statusName === 'Late Check In & Early Checkout') {
      summary.delay++
      summary.present++ // Delay is also counted as present
    } else if (statusName === 'Absent') {
      summary.absent++
    } else if (statusName === 'Leave') {
      summary.leave++
    } else if (statusName === 'Weekend') {
      summary.weekend++
    } else if (statusName === 'Holiday') {
      summary.holiday++
    }

    // Calculate expected duty hours
    if (row.shift?.in_time && row.shift?.out_time && !['Weekend', 'Holiday', 'Absent'].includes(statusName)) {
      const expected = getExpectedDutyHour(row)
      if (expected !== '-') {
        const [expHours, expMins] = expected.split(':').map(Number)
        summary.expected_duty_hours += expHours * 60 + expMins
      }
    }

    // Calculate actual duty hours
    if (row.working_hours && row.working_hours > 0) {
      summary.actual_duty_hours += Math.round(row.working_hours * 60)
    }

    // Calculate extra/less duty
    if (row.shift?.in_time && row.shift?.out_time && row.working_hours) {
      const expected = getExpectedDutyHour(row)
      if (expected !== '-') {
        const [expHours, expMins] = expected.split(':').map(Number)
        const expectedMinutes = expHours * 60 + expMins
        const actualMinutes = Math.round(row.working_hours * 60)
        summary.extra_less_duty_hours += (actualMinutes - expectedMinutes)
      }
    } else if (statusName === 'Absent' && row.shift?.in_time && row.shift?.out_time) {
      // For absent, subtract expected hours
      const expected = getExpectedDutyHour(row)
      if (expected !== '-') {
        const [expHours, expMins] = expected.split(':').map(Number)
        summary.extra_less_duty_hours -= (expHours * 60 + expMins)
      }
    }
  })

  return summary
})


const setDefaultDates = () => {
  const today = new Date()
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
  filters.from_date = startOfMonth
  filters.to_date = today
}

const loadEmployees = async () => {
  employeesLoading.value = true
  try {
    const response = await store.dispatch('getEmployees')
    employees.value = response.data.data || []
  } catch (error) {
    employees.value = []
    toast.error(error.message || 'Failed to load employees')
  } finally {
    employeesLoading.value = false
  }
}

const fetchSheet = async () => {
  if (!filters.from_date || !filters.to_date) {
    toast.warning('Please select both From Date and To Date')
    return
  }

  if (!filters.employee_id) {
    toast.error('Please select an employee')
    return
  }

  loading.value = true
  try {
    const params = {
      from_date: formatDateDash(filters.from_date),
      to_date: formatDateDash(filters.to_date),
      employee_id: filters.employee_id
    }

    const response = await store.dispatch('getEmployeeJobCard', params)
    if (response.success) {
      sheetData.value = response.data || []
      if (sheetData.value.length === 0) {
        toast.info('No attendance records found for the selected criteria')
      }
    } else {
      sheetData.value = []
      toast.error(response.message || 'Failed to fetch job card data')
    }
  } catch (error) {
    sheetData.value = []
    toast.error(error.response?.data?.message || error.message || 'Failed to fetch job card data')
  } finally {
    loading.value = false
  }
}

const resetFilters = () => {
  setDefaultDates()
  filters.employee_id = null
  sheetData.value = []
}

// Computed for company info
const getCurrentCompany = computed(() => {
  return store.getters.getCurrentCompany || {}
})

const getFileServerUrl = computed(() => {
  return store.getters.getFileServerUrl || ''
})

// Export to Excel
const exportToExcel = () => {
  if (!sheetData.value.length) {
    toast.warning('No data to export')
    return
  }

  const data = [
    ['Job Card Report'],
    [`From Date: ${formatDateView(filters.from_date)} to ${formatDateView(filters.to_date)}`]
  ]

  if (filters.employee_id) {
    const selectedEmployee = employees.value.find(e => e.id === filters.employee_id)
    if (selectedEmployee) {
      data.push([`Employee: ${selectedEmployee.name || ''}${selectedEmployee.employee_code ? ` (${selectedEmployee.employee_code})` : ''}`])
    }
  }

  data.push([], ['Attendance Date', 'Status', 'Expected Duty (Hour)', 'In Time', 'Out Time', 'Break Time (Hour)', 'Actual Duty (Hour)', 'Extra/Less Duty (Hour)', 'Remarks'], [])

  // Add data rows
  sheetData.value.forEach((row) => {
    data.push([
      (row.date),
      (row.status?.name),
      getExpectedDutyHour(row),
      formatTime(row.punch_in) || '-',
      formatTime(row.punch_out) || '-',
      formatBreakTime(row),
      formatActualDutyHour(row) || '-',
      formatExtraLessDuty(row) || '-',
      row.message || '-'
    ])
  })

  // Add summary section
  if (sheetData.value.length > 0) {
    data.push([])
    data.push(['Summary Statistics'])
    data.push(['Present:', jobCardSummary.value.present, '', 'Absent:', jobCardSummary.value.absent])
    data.push(['Delay:', jobCardSummary.value.delay, '', 'Extreme Delay:', jobCardSummary.value.extreme_delay])
    data.push(['Leave:', jobCardSummary.value.leave, '', 'Visit:', jobCardSummary.value.visit])
    data.push(['Weekend:', jobCardSummary.value.weekend, '', 'Holiday:', jobCardSummary.value.holiday])
    data.push(['Expected Duty Hour:', formatHoursMinutes(jobCardSummary.value.expected_duty_hours), '', 'Actual Duty Hour:', formatHoursMinutes(jobCardSummary.value.actual_duty_hours)])
    data.push(['Total Days (Available in office):', `${sheetData.value.length} (${jobCardSummary.value.present + jobCardSummary.value.delay})`, '', 'Extra/Less Duty Hours:', formatHoursMinutes(jobCardSummary.value.extra_less_duty_hours, true)])
  }

  const proxy = appContext?.config?.globalProperties
  if (proxy?.$exportTableToExcel && proxy?.$fileDate) {
    proxy.$exportTableToExcel(data, 'job_card_report_' + proxy.$fileDate())
  }
}

// Export to PDF
const exportToPDF = async () => {
  if (!sheetData.value.length) {
    toast.warning('No data to export')
    return
  }

  const company = getCurrentCompany.value
  const fileServerUrl = getFileServerUrl.value
  const selectedEmployee = filters.employee_id
    ? employees.value.find(e => e.id === filters.employee_id)
    : null

  let printWindow = window.open('', 'PRINT', `width=${screen.width}, height=${screen.height}, left=0, top=0`)
  printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <meta http-equiv="X-UA-Compatible" content="ie=edge">
          <title>Job Card Report</title>
          <link rel="preconnect" href="https://fonts.googleapis.com">
          <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
          <link href="https://fonts.googleapis.com/css2?family=Arimo:ital,wght@0,400..700;1,400..700&display=swap" rel="stylesheet">
          <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.8/dist/css/bootstrap.min.css" rel="stylesheet">
          <link href="https://cdn.jsdelivr.net/npm/@mdi/font@7.4.47/css/materialdesignicons.min.css" rel="stylesheet">
          <style>
            body { font-family: 'Arimo', sans-serif; margin: 0px; }
            .invoice-header { display:flex; justify-content:space-between; align-items:center; border-bottom:2px solid #eee; padding-bottom:10px; }
            .invoice-header img { max-height: 70px; max-width: 150px; }
            .table th, .table td { border: 1px solid #000; padding: 8px; vertical-align: middle; font-size: 12px; }
            .table th { background-color: #f0f0f0; color: #333; }
            .text-end { text-align: right; }
            .text-center { text-align: center; }
            h4 { font-size: 20px; color: #333; }
            p { font-size: 14px; color: #555; margin: 2px 0; }
            strong { font-weight: bold; color: #333; }
            @page { margin: 0.5in; size: letter; }
            .logo-container { display:flex; align-items:center; }
            .logo-container img { margin-left: 10px; }
            .table { width: 100%; border-collapse: collapse; table-layout: auto; }
            thead { display: table-header-group; }
            tfoot { display: none; }
            .totals-row { page-break-inside: avoid; }
            .totals-row td { font-weight: bold; background-color: #f5f5f5 !important; }
          </style>
        </head>
        <body>
          <div class="container-fluid position-relative">
            <div class="invoice-header">
              <div>
                <h4 class="text-uppercase">${company?.name || 'Company Name'}</h4>
                <p style="width:65%">${company?.address || ''}</p>
                <p><i class="mdi mdi-phone-outline"></i> ${company?.mobile || ''}</p>
                <p><i class="mdi mdi-email-outline"></i> ${company?.email || ''}</p>
                ${company?.website ? `<p><i class="mdi mdi-web"></i> ${company.website}</p>` : ''}
              </div>
              <div class="logo-container">
                ${company?.logo_thumb ? `<img src="${fileServerUrl}${company.logo_thumb}" alt="Logo" class="img-fluid">` : ''}
              </div>
            </div>
            <h4 class="text-center my-4"><strong>General Employee Job Card Report (To)</strong></h4>
            
            ${selectedEmployeeInfo.value ? `
            <div class="row mb-3">
              <div class="col-sm-6">
                <p><strong>Employee Code:</strong> ${selectedEmployeeInfo.value.employee_code || '-'}</p>
                <p><strong>Employee Name:</strong> ${selectedEmployeeInfo.value.employee_name || '-'}</p>
                <p><strong>Join Date:</strong> ${selectedEmployeeInfo.value.joining_date ? formatDateDash(selectedEmployeeInfo.value.joining_date) : '-'}</p>
               
              </div>
              <div class="col-sm-6">
                <p><strong>Designation:</strong> ${selectedEmployeeInfo.value.designation || '-'}</p>
                <p><strong>Branch:</strong> ${selectedEmployeeInfo.value.branch_name || '-'}</p>
                
                <p><strong>Department:</strong> ${selectedEmployeeInfo.value.department || '-'}</p>
              </div>
            </div>
            ` : ''}
            
            <table class="table mt-4">
              <thead>
                <tr>
                  <th>Attendance Date</th>
                  <th>Status</th>
                  <th>Expected Duty (Hour)</th>
                  <th>In Time</th>
                  <th>Out Time</th>
                  <th>Break Time (Hour)</th>
                  <th>Actual Duty (Hour)</th>
                  <th>Extra/Less Duty (Hour)</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                ${sheetData.value.map((row) => `
                  <tr>
                    <td>${(row.date)}</td>
                    <td>${(row.status?.name)}</td>
                    <td>${getExpectedDutyHour(row)}</td>
                    <td>${formatTime(row.punch_in) || '-'}</td>
                    <td>${formatTime(row.punch_out) || '-'}</td>
                    <td>${formatBreakTime(row)}</td>
                    <td>${formatActualDutyHour(row) || '-'}</td>
                    <td>${formatExtraLessDuty(row) || '-'}</td>
                    <td>${row.message || '-'}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            
            



            <table class="table table-bordered mt-4">
                <tbody>
                <tr>
                  <td><strong>Present</strong></td>
                  <td>${jobCardSummary.value.present}</td>
                  <td><strong>Absent</strong></td>
                  <td>${jobCardSummary.value.absent}</td>
                </tr>
                <tr>
                  <td><strong>Delay</strong></td>
                  <td>${jobCardSummary.value.delay}</td>
                  <td><strong>Extreme Delay</strong></td>
                  <td>${jobCardSummary.value.extreme_delay}</td>
                </tr>
                <tr>
                  <td><strong>Leave</strong></td>
                  <td>${jobCardSummary.value.leave}</td>
                  <td><strong>Visit</strong></td>
                  <td>${jobCardSummary.value.visit}</td>
                </tr>
                <tr>
                  <td><strong>Weekend</strong></td>
                  <td>${jobCardSummary.value.weekend}</td>
                  <td><strong>Holiday</strong></td>
                  <td>${jobCardSummary.value.holiday}</td>
                </tr>
                <tr>
                  <td><strong>Expected Duty Hour</strong></td>
                  <td>${formatHoursMinutes(jobCardSummary.value.expected_duty_hours)}</td>
                  <td><strong>Actual Duty Hour</strong></td>
                  <td>${formatHoursMinutes(jobCardSummary.value.actual_duty_hours)}</td>
                </tr>
                <tr>
                  <td><strong>Total Days (Available in office)</strong></td>
                  <td>${sheetData.value.length} (${jobCardSummary.value.present + jobCardSummary.value.delay})</td>
                  <td><strong>Extra/Less Duty Hours</strong></td>
                  <td>${formatHoursMinutes(jobCardSummary.value.extra_less_duty_hours, true)}</td>
                </tr>
            </tbody>
            </table>
          </div>
        </body>
      </html>
    `)
  printWindow.moveTo(0, 0)
  printWindow.focus()
  await new Promise(resolve => setTimeout(resolve, 500))
  printWindow.print()
  printWindow.close()
}

onMounted(async () => {
  setDefaultDates()
  await loadEmployees()
})
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}

.table-bordered :deep(th),
.table-bordered :deep(td) {
  border: 1px solid #000000 !important;
}

.table-bordered :deep(table) {
  border-collapse: collapse;
}

.panel-header {
  font-weight: 600;
}
</style>